/** @namespace chatStream */
import React from "react";
import {api} from "@web/lib/api";
import {LinesContext} from "./context";
import debounce from "lodash/debounce";
import {useDispatch} from "react-redux";
import {useLocation} from "wouter";

/**
 * @typedef {object} SendRequestOptionsData
 * @memberOf chatStream
 * @property {string} [workflowPath]
 * @property {string[]} [contextPaths]
 * @property {string} [subthread]
 * @property {models.Chat.Data[]|number} [history]
 * @property {string} [collectionPath]
 * @property {boolean} [isRetry]
 */

const getEndpoint = (linesContext, agentId) => {
  switch (true) {
    case linesContext.applicationId === "start":
      return process.env.NODE_ENV === "production" ?
        "https://line-v1-7zujc5azra-uw.a.run.app/guide" :
        "http://127.0.0.1:5001/admin-85663/us-west1/line-v1/guide";
    default:
      return process.env.NODE_ENV === "production" ?
        `https://line-v1-7zujc5azra-uw.a.run.app/assistant/${agentId ? agentId + "/" : ""}` :
        `http://127.0.0.1:5001/admin-85663/us-west1/line-v1/assistant/${agentId ? agentId + "/" : ""}`;
  }
};

const debouncedSendRequest = debounce((url, prompt, onData, onError) => {
  try {
    api.STREAM({url, prompt}, onData, onError);
  } catch (error) {
    console.log("Error streaming chat", error);
    onError && onError(error);
  }
}, 500, {leading: false, trailing: true});

/**
 * @function useChatStream
 * @memberof chatStream
 * @param {string} [agentId="ai-assistant"]
 * @param {boolean} [withDispatch]
 * @returns {[models.Answer.Data[], function(text:string, options:SendRequestOptionsData):void, Error, boolean, string]}
 */
function useChatStream(agentId, withDispatch) {
  const dispatch = useDispatch();
  const [answers, setAnswers] = React.useState([]);
  const [chatPath, setChatPath] = React.useState("");
  const [streamDone, setStreamDone] = React.useState(false);
  const [streamError, setStreamError] = React.useState(null);
  const linesContext = React.useContext(LinesContext);
  const [location] = useLocation();

  const onData = (stream) => {
    setChatPath(stream.path);
    setStreamDone(!!stream.done);
    setAnswers([...stream.chat?.answers]);
    withDispatch &&
    dispatch({
      type: "AI_WRITE_STREAM",
      agentId,
      stream,
      streamDone: streamDone,
    });
  }
  const onError = (error) => {
    setStreamError(error);
  }

  /**
   * @function sendRequest
   * @memberof chatStream
   * @param {string} text
   * @param {SendRequestOptionsData} options
   * @return {void}
   */
  function sendRequest(text, options){
    const {workflowPath, taskPath, actionRunPath, workflowRunPath, contextPaths = [], history, subthread, isRetry} = options;
    setStreamError(null);
    setStreamDone(false);
    !!text &&
    debouncedSendRequest(getEndpoint(linesContext, agentId), {
      text,
      subthread,
      // linesContext,
      location,
      workflowPath,
      taskPath,
      actionRunPath,
      workflowRunPath,
      history: history ?? 0,
      contextPaths,
      isRetry,
    }, onData, onError,);
  }

  return [answers, sendRequest, streamError, streamDone, chatPath];
}

export default useChatStream;
